/**
 * Boot restore (ADR-0038, MICS-33).
 *
 * ADR-0036 made the cookie refresh the one thing a fresh page load restores
 * from. Where the server runs `AUTH_REFRESH_COOKIE_MODE=off` — dev, whose two
 * SPA hosts share no registrable domain — that call can never succeed, so every
 * reload, duplicated tab and new tab arrived signed out. This adds the second
 * source a tab can restore from on its own: the issuer id_token in
 * `localStorage`, re-exchanged for a Penn session exactly as a 401 does.
 *
 * Order matters: the cookie path first (cheap, no identity round trip,
 * re-reads role + permissions server-side), the re-exchange only as fallback.
 * Boot NEVER ends the issuer session — see the `identity-rejected` branch.
 *
 * Third source (ISC-403): where the re-exchange reports `no-token` because the
 * stored id_token is past its 1h `exp`, the 24h SSO cookie may still be live,
 * so a `prompt=none` attempt restores the session with no user interaction
 * instead of landing on the passive /login. That attempt is a navigation, not
 * a value — it never produces a BootRestore here; the callback completes it.
 */
import type { AuthUser } from "../types/auth-types/AuthUser";
import {
  TOKEN_STORAGE_KEY,
  USER_STORAGE_KEY,
} from "../config/auth-storage-keys";
import { authStorage } from "../config/session-storage";
import { attemptReExchange } from "./AxiosService";
import { restoreSession } from "./session-refresh";
import { beginSilentReauthIfExpired } from "./silent-reauth";

export interface BootRestore {
  accessToken: string;
  user: AuthUser;
}


export async function restoreSessionAtBoot(): Promise<BootRestore | null> {
  // restoreSession() self-guards on AUTH_COOKIE_SESSION (returns null when the
  // flag is off), so the re-exchange below runs in both modes.
  const cookieOutcome = await restoreSession();
  if (cookieOutcome) {
    return { accessToken: cookieOutcome.accessToken, user: cookieOutcome.user };
  }

  const result = await attemptReExchange();
  switch (result.outcome) {
    case "refreshed":
      return { accessToken: result.token, user: result.user };
    case "no-token":
      // An id_token past its `exp` with a live 24h SSO cookie is recoverable
      // without the user: ask the issuer silently (ISC-403). Navigates away
      // when it fires, so nothing below runs. Anonymous tabs (no stored token)
      // and an already-suppressed retry fall through to the teardown.
      if (await beginSilentReauthIfExpired()) return null;
      // Anonymous, or an issuer token past its `exp`. Drop the cached user: it
      // is what seeds the next load's `selectedRegion` and the `x-auth-scope`
      // header, neither of which should outlive the session it came from.
      authStorage.removeItem(USER_STORAGE_KEY);
      return null;
    case "transient":
      // A network or 5xx failure of the RE-EXCHANGE, not a verdict: leave
      // everything in place so the next request can still recover. The cookie
      // leg cannot report this — session-refresh collapses every error to null.
      return null;
    default:
      // "identity-rejected": svc-02 refused the identity. Local teardown only —
      // ending the platform SSO for every app on the strength of a boot-time
      // refusal is not this module's call, and /login is passive.
      authStorage.removeItem(TOKEN_STORAGE_KEY);
      authStorage.removeItem(USER_STORAGE_KEY);
      return null;
  }
}
